import ct from "countries-and-timezones";
import { startOfDay } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

let cachedTimezoneOptions = null;

export const getTimezoneOptions = () => {
  if (cachedTimezoneOptions) {
    return cachedTimezoneOptions;
  }

  cachedTimezoneOptions = Object.values(ct.getAllTimezones())
    .filter((tz) => !tz.deprecated && !tz.aliasOf)
    .map((tz) => ({
      value: tz.name,
      label: `${tz.name} (UTC${tz.utcOffsetStr})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return cachedTimezoneOptions;
};

export const isValidTimezone = (timezone) => {
  if (!timezone || typeof timezone !== "string") {
    return false;
  }

  try {
    Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

export const resolveTimezone = (timezone) =>
  isValidTimezone(timezone) ? timezone : getBrowserTimezone();

const toDate = (date) => {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof date === "number") {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export const toZonedDisplayDate = ({ date, timezone }) => {
  const parsed = toDate(date);
  if (!parsed) {
    return null;
  }

  return utcToZonedTime(parsed, resolveTimezone(timezone));
};

export const toUtcTimestamp = ({ date, timezone }) => {
  const parsed = toDate(date);
  if (!parsed) {
    return null;
  }

  return zonedTimeToUtc(parsed, resolveTimezone(timezone)).getTime();
};

const WALL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const WALL_TIME_PATTERN = /^\d{2}:\d{2}$/;

export const wallClockToDisplayDate = ({ date, time }) => {
  if (
    !WALL_DATE_PATTERN.test(date || "") ||
    !WALL_TIME_PATTERN.test(time || "")
  ) {
    return null;
  }

  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  // DateTimeField reads getHours()/getMinutes(); those local fields ARE the
  // civil wall clock. Do not round-trip Date.UTC — in Bogotá that turns 19:00
  // into 14:00 (UTC−5).
  return new Date(year, month - 1, day, hours, minutes, 0);
};

export const getZonedStartOfDay = ({ date = new Date(), timezone }) => {
  const zonedDate = toZonedDisplayDate({ date, timezone });
  return startOfDay(zonedDate);
};

export const getZonedNow = ({ timezone }) =>
  toZonedDisplayDate({ date: new Date(), timezone });

export const isSameZonedDay = ({ dateA, dateB, timezone }) => {
  const dayA = getZonedStartOfDay({ date: dateA, timezone });
  const dayB = getZonedStartOfDay({ date: dateB, timezone });
  return dayA.getTime() === dayB.getTime();
};

export const isZonedDayBeforeToday = ({ date, timezone }) => {
  const dayStart = getZonedStartOfDay({ date, timezone });
  const todayStart = getZonedStartOfDay({ timezone });
  return dayStart < todayStart;
};
